import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  Activity, 
  Clock, 
  Zap, 
  AlertTriangle, 
  TrendingUp, 
  RefreshCw,
  Database,
  Server
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { formatDistanceToNow } from 'date-fns';
import { sv } from 'date-fns/locale';

interface PerformanceLog {
  id: string;
  event_type: string;
  source: string;
  user_id: string | null;
  revision_id: string | null;
  batch_id: string | null;
  duration_ms: number | null;
  tokens_in: number;
  tokens_out: number;
  status: string;
  error_message: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

interface PerformanceStats {
  totalEvents: number;
  avgDurationMs: number;
  errorRate: number;
  totalTokensIn: number;
  totalTokensOut: number;
  eventsPerMinute: number;
}

export default function AdminPerformancePage() {
  const { t } = useTranslation();
  const [logs, setLogs] = useState<PerformanceLog[]>([]);
  const [stats, setStats] = useState<PerformanceStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<'1h' | '24h' | '7d'>('24h');
  const [eventTypeFilter, setEventTypeFilter] = useState<string>('all');

  const fetchLogs = async () => {
    setLoading(true);
    try {
      // Calculate time range
      const now = new Date();
      let startTime: Date;
      switch (timeRange) {
        case '1h':
          startTime = new Date(now.getTime() - 60 * 60 * 1000);
          break;
        case '7d':
          startTime = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        default:
          startTime = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      }

      let query = supabase
        .from('performance_logs')
        .select('*')
        .gte('created_at', startTime.toISOString())
        .order('created_at', { ascending: false })
        .limit(500);

      if (eventTypeFilter !== 'all') {
        query = query.eq('event_type', eventTypeFilter);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching performance logs:', error);
        return;
      }

      const typedData = (data || []) as PerformanceLog[];
      setLogs(typedData);

      // Calculate stats
      if (typedData.length > 0) {
        const durations = typedData
          .filter(l => l.duration_ms !== null)
          .map(l => l.duration_ms as number);
        
        const errorCount = typedData.filter(l => l.status === 'error').length;
        const timeRangeMs = now.getTime() - startTime.getTime();
        const minutesInRange = timeRangeMs / (60 * 1000);

        setStats({
          totalEvents: typedData.length,
          avgDurationMs: durations.length > 0 
            ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length) 
            : 0,
          errorRate: typedData.length > 0 
            ? Math.round((errorCount / typedData.length) * 100 * 10) / 10 
            : 0,
          totalTokensIn: typedData.reduce((sum, l) => sum + (l.tokens_in || 0), 0),
          totalTokensOut: typedData.reduce((sum, l) => sum + (l.tokens_out || 0), 0),
          eventsPerMinute: Math.round((typedData.length / minutesInRange) * 100) / 100
        });
      } else {
        setStats(null);
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [timeRange, eventTypeFilter]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return <Badge className="bg-green-100 text-green-800">Success</Badge>;
      case 'error':
        return <Badge variant="destructive">Error</Badge>;
      case 'retry':
        return <Badge className="bg-yellow-100 text-yellow-800">Retry</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getSourceIcon = (source: string) => {
    switch (source) {
      case 'deno_worker':
        return <Server className="h-4 w-4" />;
      case 'edge_function':
        return <Zap className="h-4 w-4" />;
      case 'frontend':
        return <Activity className="h-4 w-4" />;
      default:
        return <Database className="h-4 w-4" />;
    }
  };

  const formatDuration = (ms: number | null) => {
    if (ms === null) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}min`;
  };

  const formatTokens = (count: number) => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count.toString();
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Prestanda & Övervakning</h1>
          <p className="text-muted-foreground">Global systemövervakning för alla användare</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={timeRange} onValueChange={(v: '1h' | '24h' | '7d') => setTimeRange(v)}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1h">Senaste timmen</SelectItem>
              <SelectItem value="24h">Senaste 24h</SelectItem>
              <SelectItem value="7d">Senaste 7 dagar</SelectItem>
            </SelectContent>
          </Select>
          <Select value={eventTypeFilter} onValueChange={setEventTypeFilter}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alla händelser</SelectItem>
              <SelectItem value="batch_complete">Batch Complete</SelectItem>
              <SelectItem value="analysis_request">Analysis Request</SelectItem>
              <SelectItem value="revision_complete">Revision Complete</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={fetchLogs} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Totala händelser</CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalEvents}</div>
              <p className="text-xs text-muted-foreground">
                {stats.eventsPerMinute}/min i genomsnitt
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Genomsnittlig tid</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatDuration(stats.avgDurationMs)}</div>
              <p className="text-xs text-muted-foreground">
                Per operation
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Felfrekvens</CardTitle>
              <AlertTriangle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${stats.errorRate > 5 ? 'text-destructive' : ''}`}>
                {stats.errorRate}%
              </div>
              <p className="text-xs text-muted-foreground">
                {stats.errorRate <= 5 ? 'Normal nivå' : 'Bör undersökas'}
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Token-användning</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatTokens(stats.totalTokensIn + stats.totalTokensOut)}
              </div>
              <p className="text-xs text-muted-foreground">
                In: {formatTokens(stats.totalTokensIn)} | Out: {formatTokens(stats.totalTokensOut)}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle>Senaste händelser</CardTitle>
          <CardDescription>Detaljerad logg över systemhändelser</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Inga loggposter hittades för vald tidsperiod
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tid</TableHead>
                    <TableHead>Källa</TableHead>
                    <TableHead>Typ</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Tid</TableHead>
                    <TableHead className="text-right">Tokens</TableHead>
                    <TableHead>Detaljer</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.slice(0, 100).map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                        {formatDistanceToNow(new Date(log.created_at), { 
                          addSuffix: true, 
                          locale: sv 
                        })}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {getSourceIcon(log.source)}
                          <span className="text-xs">{log.source}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs">{log.event_type}</TableCell>
                      <TableCell>{getStatusBadge(log.status)}</TableCell>
                      <TableCell className="text-right text-xs">
                        {formatDuration(log.duration_ms)}
                      </TableCell>
                      <TableCell className="text-right text-xs">
                        {log.tokens_in > 0 || log.tokens_out > 0 ? (
                          <span>{formatTokens(log.tokens_in + log.tokens_out)}</span>
                        ) : '-'}
                      </TableCell>
                      <TableCell className="text-xs max-w-xs truncate">
                        {log.error_message || (
                          log.metadata && Object.keys(log.metadata).length > 0
                            ? JSON.stringify(log.metadata).substring(0, 50) + '...'
                            : '-'
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
